import React, { useCallback, useEffect, useMemo, useState } from "react";
import { format, addDays, startOfDay, parseISO } from "date-fns";
import { pt } from "date-fns/locale";
import { Trash2, CalendarOff, Plus, Pencil, Check, X as XIcon } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

// JS Date.getDay(): Sun=0, Mon=1, Tue=2 ... Closed as weekly schedule
const JS_WEEKLY_CLOSED = new Set([0, 1, 2]);
const WEEKDAY_PT = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];

export default function ClosedDatesDialog({ open, onOpenChange }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editingDate, setEditingDate] = useState(null);
  const [editReason, setEditReason] = useState("");

  const minDate = useMemo(() => startOfDay(new Date()), []);
  const maxDate = useMemo(() => addDays(new Date(), 365), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/closed-dates");
      setList(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro ao carregar.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) { load(); setDate(null); setReason(""); }
  }, [open, load]);

  const addDate = async () => {
    if (!date) { toast.error("Escolha uma data."); return; }
    setSubmitting(true);
    try {
      await api.post("/closed-dates", { date: format(date, "yyyy-MM-dd"), reason: reason.trim() });
      toast.success("Dia encerrado com sucesso.");
      setDate(null); setReason("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro ao encerrar dia.");
    } finally {
      setSubmitting(false);
    }
  };

  const removeDate = async (d) => {
    try {
      await api.delete(`/closed-dates/${d}`);
      toast.success("Dia reaberto.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro ao reabrir.");
    }
  };

  const startEdit = (d) => {
    setEditingDate(d.date);
    setEditReason(d.reason || "");
  };

  const cancelEdit = () => {
    setEditingDate(null);
    setEditReason("");
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/closed-dates/${editingDate}`, { reason: editReason.trim() });
      toast.success("Observações atualizadas.");
      setEditingDate(null);
      setEditReason("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro ao atualizar.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-[#F4EFEB] w-[95vw] max-w-3xl rounded-none flex flex-col max-h-[85dvh] p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-white/5 shrink-0">
          <DialogTitle className="font-display text-3xl text-[#F4EFEB] flex items-center gap-3 pr-8">
            <CalendarOff className="h-6 w-6 text-[#D45A2C]" /> Encerrar dias
          </DialogTitle>
          <DialogDescription className="text-[#A19D98] text-sm">
            Marque dias em que o restaurante não vai aceitar reservas (eventos privados, feriados, férias, etc.).
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-5 space-y-4">

        {/* Add new */}
        <div className="border border-white/5 bg-[#0E0E0E] p-5 space-y-4" data-testid="add-closed-date">
          <Label className="text-xs uppercase tracking-[0.22em] text-[#F4EFEB]">Nova data encerrada</Label>

          <div className="grid md:grid-cols-[auto_1fr] gap-5 items-start">
            {/* Inline calendar */}
            <div className="bg-[#0A0A0A] border border-white/5 p-2" data-testid="closed-date-calendar">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={(d) => d < minDate || d > maxDate || JS_WEEKLY_CLOSED.has(d.getDay())}
                locale={pt}
                className="text-[#F4EFEB]"
              />
            </div>

            <div className="space-y-3">
              <div className="text-xs text-[#A19D98]">
                {date ? (
                  <>Data escolhida: <span className="text-[#D45A2C] font-display text-lg">{format(date, "d 'de' MMMM yyyy", { locale: pt })}</span></>
                ) : (
                  <span className="text-[#6b6763] uppercase tracking-widest">Escolha um dia no calendário</span>
                )}
              </div>
              <Input
                data-testid="closed-date-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Motivo/observações (opcional): evento privado, feriado..."
                className="lm-input"
              />
              <Button
                onClick={addDate}
                disabled={!date || submitting}
                data-testid="btn-add-closed-date"
                className="bg-[#D45A2C] hover:bg-[#B84B23] text-white rounded-none px-4 text-xs uppercase tracking-[0.25em] w-full md:w-auto"
              >
                <Plus className="h-4 w-4 mr-1" /> Encerrar este dia
              </Button>
              <p className="text-[10px] text-[#6b6763] uppercase tracking-widest pt-1">
                Nota: Domingo, segunda e terça já são encerramento semanal.
              </p>
            </div>
          </div>
        </div>

        {/* List */}
        <div data-testid="closed-dates-list">
          {loading ? (
            <p className="text-center text-[#6b6763] uppercase tracking-widest text-xs py-8">A carregar...</p>
          ) : list.length === 0 ? (
            <p className="text-center text-[#6b6763] uppercase tracking-widest text-xs py-8">Sem dias encerrados manualmente.</p>
          ) : (
            <div className="divide-y divide-white/5 border border-white/5">
              {list.map((d) => {
                const dt = parseISO(d.date);
                const wd = WEEKDAY_PT[dt.getDay()];
                const isEditing = editingDate === d.date;
                return (
                  <div key={d.date} className="px-4 py-3" data-testid={`closed-row-${d.date}`}>
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="text-[#F4EFEB]">
                          <span className="font-display text-lg">{format(dt, "d 'de' MMMM yyyy", { locale: pt })}</span>
                          <span className="ml-2 text-[10px] uppercase tracking-widest text-[#6b6763]">· {wd}</span>
                        </div>
                        {!isEditing && d.reason && <div className="text-xs text-[#A19D98] mt-0.5">{d.reason}</div>}
                        {!isEditing && !d.reason && <div className="text-xs text-[#6b6763] italic mt-0.5">Sem observações</div>}
                      </div>
                      {!isEditing ? (
                        <div className="flex gap-2">
                          <button onClick={() => startEdit(d)} data-testid={`btn-edit-closed-${d.date}`}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-widest border border-white/10 text-[#A19D98] hover:border-[#D45A2C]/50 hover:text-[#D45A2C] transition-colors">
                            <Pencil className="h-3.5 w-3.5" /> Editar
                          </button>
                          <button onClick={() => removeDate(d.date)} data-testid={`btn-remove-closed-${d.date}`}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-widest border border-white/10 text-[#A19D98] hover:border-[#FF3B30]/40 hover:text-[#FF3B30] transition-colors">
                            <Trash2 className="h-3.5 w-3.5" /> Reabrir
                          </button>
                        </div>
                      ) : null}
                    </div>
                    {isEditing && (
                      <div className="mt-3 flex flex-col gap-2 bg-[#0E0E0E] p-3 border border-[#D45A2C]/20">
                        <Label className="text-[10px] uppercase tracking-[0.22em] text-[#F4EFEB]">Observações</Label>
                        <Input
                          value={editReason}
                          onChange={(e) => setEditReason(e.target.value)}
                          placeholder="ex: evento privado, feriado, férias..."
                          data-testid={`input-edit-reason-${d.date}`}
                          className="lm-input"
                          autoFocus
                        />
                        <div className="flex justify-end gap-2">
                          <button onClick={cancelEdit} data-testid={`btn-cancel-edit-${d.date}`}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-widest border border-white/10 text-[#A19D98] hover:text-[#F4EFEB] transition-colors">
                            <XIcon className="h-3.5 w-3.5" /> Cancelar
                          </button>
                          <button onClick={saveEdit} data-testid={`btn-save-edit-${d.date}`}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-widest bg-[#D45A2C] hover:bg-[#B84B23] text-white">
                            <Check className="h-3.5 w-3.5" /> Guardar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
